import React, { useEffect, useRef } from "react";
import { Box, Typography, CircularProgress, useTheme, List, ListItem, ListItemText, styled } from "@mui/material";
import ChatMessageComponent, { ChatMessage } from "./ChatMessage";

interface ChatHistoryProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ messages, isLoading = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed",
          borderColor: "grey.300",
          borderRadius: 2,
          backgroundColor: isDarkMode ? "grey.200" : "grey.50",
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "left" }}>
          This chat allows you to interact with financial system. You can:
          <List>
            <ListItemTextStyled>
              <ListItemText primary="Ask about email content" />
            </ListItemTextStyled>
            <ListItemTextStyled>
              <ListItemText primary="Look up contacts by company name" />
            </ListItemTextStyled>
            <ListItemTextStyled>
              <ListItemText primary="Find invoices by number, contact, or status" />
            </ListItemTextStyled>
            <ListItemTextStyled>
              <ListItemText primary="Check latest payments for a contact" />
            </ListItemTextStyled>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "left" }}>
            The AI turns your questions into specific searches. Vague questions may give incomplete or unexpected
            results.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "left" }}>
            Examples:
            <List>
              <ListItemTextStyled>
                <ListItemText primary="Has customer A paid all invoices?" sx={{ fontStyle: "italic" }} />
              </ListItemTextStyled>
              <ListItemTextStyled>
                <ListItemText primary="List unpaid invoices for A." sx={{ fontStyle: "italic" }} />
              </ListItemTextStyled>
            </List>
          </Typography>
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 400,
        overflowY: "auto",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        p: 1,
        backgroundColor: "background.paper",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "grey.100",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "grey.400",
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: "grey.500",
          },
        },
      }}
    >
      {messages.map((message) => (
        <ChatMessageComponent key={message.id} message={message} />
      ))}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mb: 1,
            alignItems: "center",
            gap: 1,
          }}
        >
          <CircularProgress size={16} color="primary" />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Attelas is thinking...
          </Typography>
        </Box>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatHistory;

const ListItemTextStyled = styled(ListItem)({
  paddingTop: 0,
  paddingBottom: 0,
  position: "relative",
  "&:before": {
    content: "'-'",
    position: "absolute",
    top: "4px",
    left: "5px",
  },
});
